import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared'
import { Col, Visible, Row, config } from 'react-awesome-styled-grid'
import type { ContentBlockHorizontalImageAndTextType } from '../../../services/contentfulMarketingLandingPages'
import SharedCta from '../SharedCta'
import { FadeInImage } from '@components/shared/FadeInImage'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { MARKS } from '@contentful/rich-text-types'
import { landingPageBackgroundColor } from '../../../services/contentfulMarketingLandingPages'
import { BlockSection } from './sectionStyles'
import { OutlineOnFocusVisible } from '@components/shared/buttons'

const options = {
  renderNode: {
    [MARKS.CODE]: (node: any, children: any): React.ReactNode => {
      return <div dangerouslySetInnerHTML={{ __html: children }} />
    }
  }
}

export default function HorizontalImageAndText (props: {
  block: ContentBlockHorizontalImageAndTextType,
  isFirst: boolean,
  isSingleContentBlock: boolean
}): React.ReactNode {
  const {
    id,
    backgroundImage,
    backgroundColor,
    squareImage,
    imageWidth,
    imageHeight,
    headerText,
    descriptionText,
    cta,
    isImageOnBottomAtMobileBreakpoint,
    imageLink
  } = props.block

  const description = documentToReactComponents(descriptionText!, options)

  let offSet
  if (squareImage) {
    offSet = { md: 0.5 }
  }

  const SquareImage = (): React.ReactNode => {
    if (!squareImage) return null
    if (imageLink) {
      return (
        <ImageContainerLink target="_blank" rel="noopener noreferrer" href={`${imageLink}`}>
          <FadeInImage
            src={`https:${squareImage}`}
            height={imageHeight || 650}
            width={imageWidth || 650}
            fadeInTime={0.3}
          />
        </ImageContainerLink>
      )
    }
    return (
      <ImageContainer>
        <FadeInImage
          src={`https:${squareImage}`}
          height={imageHeight || 650}
          width={imageWidth || 650}
          fadeInTime={0.3}
        />
      </ImageContainer>
    )
  }

  const isWhiteText =
    backgroundColor === landingPageBackgroundColor.glow ||
    props.isSingleContentBlock
  const isLargeText = props.isSingleContentBlock

  return (
    <BlockSection
      sectionId={id}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      isSingleContentBlock={props.isSingleContentBlock}
      isFirst={props.isFirst}
    >
      <Row align='center' justify='center'>
        {!!squareImage && (
          <Visible xs={false} sm={false} md={true} lg={true} xl={true}>
            <Col
              md={3.5}
              lg={4.5}
              offset={{ lg: 0.5 }}
              align={{ md: 'flex-end' }}
            >
              <SquareImage />
            </Col>
          </Visible>
        )}
        <Col
          xs={4}
          sm={8}
          md={4}
          lg={6}
          offset={offSet}
          align={{ xs: 'center', md: 'flex-start' }}
        >
          <TextContainer>
            {!!headerText && (
              <Header isWhiteText={isWhiteText} isLargeText={isLargeText}>
                {headerText}
              </Header>
            )}

            {!!squareImage && !isImageOnBottomAtMobileBreakpoint && (
              <Visible xs={true} sm={true} md={false} lg={false} xl={false}>
                <SquareImage />
              </Visible>
            )}

            {!!description && (
              <Description isWhiteText={isWhiteText} isLargeText={isLargeText}>
                {description}
              </Description>
            )}

            {!!cta && (
              <CtaContainer>
                <SharedCta cta={cta} />
              </CtaContainer>
            )}
          </TextContainer>
        </Col>

        {!!squareImage && isImageOnBottomAtMobileBreakpoint && (
          <Visible xs={true} sm={true} md={false} lg={false} xl={false}>
            <Col
              xs={isImageOnBottomAtMobileBreakpoint ? 2 : 4}
              sm={isImageOnBottomAtMobileBreakpoint ? 4 : 8}
              align={{ xs: 'center', md: 'flex-start' }}
            >
              <BottomImageContainer>
                <SquareImage />
              </BottomImageContainer>
            </Col>
          </Visible>
        )}
      </Row>
    </BlockSection>
  )
}

const BottomImageContainer = styled.div<any>`
  margin: 27px 0 0;
`
const ImageContainer = styled.div<any>`
  overflow: hidden;
  position: relative;

  margin: 0 0 40px 0;
  ${(props: any): any => config(props).media.md`
    margin: 0;
  `}
`
const ImageContainerLink = styled.a<any>`
  overflow: hidden;
  position: relative;
  margin: 0 0 40px 0;
  ${(props: any): any => config(props).media.md`
    margin: 0;
  `}
  transition: opacity .6s cubic-bezier(0.19,1,0.22,1);
  &:hover,
  &:focus {
    opacity: 0.8;
  }
  ${OutlineOnFocusVisible}
`
const TextContainer = styled.div<any>`
  max-width: 650px;
`
const Header = styled.h1<any>`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Queens Condensed Light';
  font-style: normal;
  font-weight: normal;
  line-height: ${(props: { isLargeText: boolean }): number =>
    props.isLargeText ? 105 : 120}%;
  margin: 0 0 20px 0;
  padding: 0;

  text-align: center;
  font-size: ${(props: { isLargeText: boolean }): number =>
    props.isLargeText ? 45 : 36}px;

  ${(props: any): any => config(props).media.md`
    text-align: left;
    font-size: ${(props: { isLargeText: boolean }): number =>
      props.isLargeText ? 70 : 50}px;
  `}

  ${(props: any): any => config(props).media.lg`
    font-size: ${(props: { isLargeText: boolean }): number =>
      props.isLargeText ? 90 : 50}px;
  `}
`
const Description = styled.div<any>`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Good Sans Light';
  font-style: normal;
  font-weight: normal;
  line-height: 150%;
  padding: 0;
  margin: auto;
  width: 95%;

  text-align: center;
  ${(props: any): any => config(props).media.md`
    width: 100%;
    text-align: left;
    font-size: ${(props: { isLargeText: boolean }): number =>
      props.isLargeText ? 18 : 16}px;
  `}

  ol, li, ul, blockquote,img {
    display: none;
  }
  b {
    font-family: 'Good Sans Bold';
  }
`
const CtaContainer = styled.div<any>`
  display: flex;

  justify-content: center;
  margin: 30px -5px 0;
  ${(props: any): any => config(props).media.md`
    justify-content: flex-start;
    margin: 35px -10px 0;
  `}
`
